import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from "recharts";
import { Loader2, BarChart3 } from "lucide-react";

export default function StatsByDisciplinePanel() {
  const [competitions, setCompetitions] = useState([]);
  const [cid, setCid] = useState("all");
  const [kind, setKind] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/competitions/all").then((r) => setCompetitions(r.data || []));
  }, []);

  useEffect(() => {
    const params = {};
    if (cid !== "all") params.cid = cid;
    if (kind !== "all") params.kind = kind;
    setLoading(true);
    api
      .get("/stats/by-discipline", { params })
      .then((r) => setData(r.data))
      .catch(() => setData({ total: 0, items: [] }))
      .finally(() => setLoading(false));
  }, [cid, kind]);

  const chartHeight = useMemo(() => {
    const n = data?.items?.length || 0;
    return Math.max(280, n * 32 + 60);
  }, [data]);

  const BAR_COLORS = ["#FF5722", "#00E5FF", "#A855F7", "#F59E0B", "#22C55E", "#EC4899"];

  return (
    <div data-testid="stats-panel" className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-[#0E0E11] border border-white/10 rounded-2xl p-4">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-white/50">Competizione</span>
          <Select value={cid} onValueChange={setCid}>
            <SelectTrigger
              data-testid="stats-filter-competition"
              className="bg-white/5 border-white/10 text-white min-w-[220px]"
            >
              <SelectValue placeholder="Tutte" />
            </SelectTrigger>
            <SelectContent className="bg-[#0E0E11] border-white/10 text-white max-h-72">
              <SelectItem value="all" className="text-white hover:bg-white/10">
                Tutte le competizioni
              </SelectItem>
              {competitions.map((c) => (
                <SelectItem key={c.id} value={c.id} className="text-white hover:bg-white/10">
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-white/50">Tipo</span>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger
              data-testid="stats-filter-kind"
              className="bg-white/5 border-white/10 text-white min-w-[160px]"
            >
              <SelectValue placeholder="Tutti" />
            </SelectTrigger>
            <SelectContent className="bg-[#0E0E11] border-white/10 text-white">
              <SelectItem value="all" className="text-white hover:bg-white/10">Tutti</SelectItem>
              <SelectItem value="musica" className="text-white hover:bg-white/10">Solo Musica</SelectItem>
              <SelectItem value="video" className="text-white hover:bg-white/10">Solo Video</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="ml-auto flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#FF5722]/20 to-[#00E5FF]/20 border border-cyan-500/30">
          <BarChart3 className="w-4 h-4 text-cyan-300" />
          <span className="text-sm text-white/70">
            Totale invii:{" "}
            <span
              data-testid="stats-total"
              className="font-bold text-white"
            >
              {data?.total ?? 0}
            </span>
          </span>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-[#0E0E11] border border-white/10 rounded-2xl p-4">
        {loading ? (
          <div className="p-10 text-center text-white/50 flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Caricamento...
          </div>
        ) : !data?.items?.length ? (
          <div data-testid="stats-empty" className="p-10 text-center text-white/50">
            Nessun invio registrato per i filtri selezionati.
          </div>
        ) : (
          <>
            <div className="mb-3 text-sm font-semibold text-white/80 tracking-wide uppercase">
              Invii per Disciplina — {data.items.length} discipline
            </div>
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart
                data={data.items}
                layout="vertical"
                margin={{ top: 10, right: 30, bottom: 10, left: 20 }}
                barCategoryGap={6}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  type="number"
                  stroke="rgba(255,255,255,0.4)"
                  allowDecimals={false}
                  tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 12 }}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={180}
                  stroke="rgba(255,255,255,0.4)"
                  tick={{ fill: "rgba(255,255,255,0.75)", fontSize: 12 }}
                />
                <Tooltip
                  cursor={{ fill: "rgba(255,87,34,0.08)" }}
                  contentStyle={{
                    background: "#0E0E11",
                    border: "1px solid rgba(255,255,255,0.15)",
                    borderRadius: 12,
                    color: "#fff",
                  }}
                  labelStyle={{ color: "#fff", fontWeight: 600 }}
                />
                <Legend wrapperStyle={{ color: "rgba(255,255,255,0.6)" }} />
                {kind === "all" ? (
                  <>
                    <Bar dataKey="musica" stackId="a" fill="#FF5722" name="Musica" radius={[4, 0, 0, 4]} />
                    <Bar dataKey="video" stackId="a" fill="#00E5FF" name="Video" radius={[0, 4, 4, 0]} />
                  </>
                ) : (
                  <Bar dataKey="count" name={kind === "musica" ? "Musica" : "Video"} radius={[0, 4, 4, 0]}>
                    {data.items.map((_, i) => (
                      <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                    ))}
                  </Bar>
                )}
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </div>
    </div>
  );
}
